import { useState, useEffect, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import {
  PlusCircle,
  TrendingUp,
  Users,
  DollarSign,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Percent,
  Heart,
  Infinity,
  UserMinus,
} from "lucide-react";
import CompanyTable from "../../components/internal/CompanyTable";
import DashboardFilters from "../../components/internal/DashboardFilters";
import TrendChart, {
  generateTrendData,
} from "../../components/internal/TrendChart";
import CFOAssistant from "../../components/internal/CFOAssistant";
import BillingAuditPanel from "../../components/internal/BillingAuditPanel";
import CompanyFormModal from "../../components/internal/CompanyFormModal";
import { apiFetch } from "@/lib/api";

const BILLING_BATCH_SIZE = 50;

export default function Dashboard() {
  const { companies: layoutCompanies, refreshCompanies } =
    useOutletContext() || {};
  const [billingData, setBillingData] = useState({});
  const [isLoading, setIsLoading] = useState(!layoutCompanies?.length);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null); // null = create, object = edit
  const [availablePlans, setAvailablePlans] = useState([]);

  const [filters, setFilters] = useState({
    period: "all",
    status: "all",
    plan: "all",
  });

  const allCompanies = layoutCompanies || [];

  // Apply filters
  const companies = useMemo(() => {
    let filtered = allCompanies;

    // Period filter
    if (filters.period !== "all") {
      const now = new Date();
      const monthsBack =
        { "1m": 1, "3m": 3, "6m": 6, "12m": 12 }[filters.period] || 0;
      if (monthsBack > 0) {
        const cutoff = new Date(
          now.getFullYear(),
          now.getMonth() - monthsBack,
          1,
        );
        filtered = filtered.filter(
          (c) => c.createdAt && new Date(c.createdAt) >= cutoff,
        );
      }
    }

    // Status filter
    if (filters.status !== "all") {
      if (filters.status === "overdue") {
        filtered = filtered.filter(
          (c) => billingData?.[c.cnpj]?.financial?.status === "overdue",
        );
      } else {
        filtered = filtered.filter(
          (c) => (c.status || "active") === filters.status,
        );
      }
    }

    // Plan filter
    if (filters.plan !== "all") {
      filtered = filtered.filter((c) => c.planType === filters.plan);
    }

    return filtered;
  }, [allCompanies, filters, billingData]);

  // Trend data for chart (always use all companies, not filtered)
  const trendData = useMemo(
    () => generateTrendData(allCompanies, billingData),
    [allCompanies, billingData],
  );

  const fetchBillingData = async (companiesList) => {
    const cnpjs = companiesList.map((c) => c.cnpj).filter(Boolean);

    if (cnpjs.length === 0) return;

    try {
      const nextBillingData = {};

      for (let index = 0; index < cnpjs.length; index += BILLING_BATCH_SIZE) {
        const batch = cnpjs.slice(index, index + BILLING_BATCH_SIZE);
        const res = await apiFetch(
          `/api/internal/billing/batch?cnpjs=${batch.join(",")}`,
        );

        if (!res.ok) {
          throw new Error(
            `Billing batch request failed with status ${res.status}`,
          );
        }

        const data = await res.json();
        Object.assign(nextBillingData, data);
      }

      setBillingData(nextBillingData);
    } catch (error) {
      console.error("Failed to fetch billing data", error);
    }
  };

  // Quando companies chegam do layout, buscar billing e atualizar loading
  useEffect(() => {
    if (allCompanies.length > 0) {
      setIsLoading(false);
      fetchBillingData(allCompanies);
    }
  }, [allCompanies]);

  // Fetch all plans (including non-public) for admin dashboard
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await apiFetch("/api/plans/admin/all");
        if (res.ok) {
          const data = await res.json();
          setAvailablePlans(data.map((p) => ({ id: p.slug, name: p.name })));
        }
      } catch (error) {
        console.error("Failed to fetch plans", error);
      }
    };
    fetchPlans();
  }, []);

  const openCreateModal = () => {
    setEditingCompany(null);
    setShowModal(true);
  };

  const openEditModal = (company) => {
    setEditingCompany(company);
    setShowModal(true);
  };

  const closeModal = () => setShowModal(false);

  const handleModalSaved = () => {
    setShowModal(false);
    setEditingCompany(null);
    if (refreshCompanies) refreshCompanies();
  };

  // ========================================
  // SaaS Metrics Computation
  // ========================================
  const metrics = useMemo(() => {
    if (!companies.length) return null;

    const now = new Date();
    const thisMonth = now.toISOString().slice(0, 7); // YYYY-MM
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      .toISOString()
      .slice(0, 7);

    let mrr = 0;
    let activeCount = 0;
    let churnedCount = 0;
    let trialCount = 0;
    let trialExpiredCount = 0;
    let overdueCount = 0;
    let newThisMonth = 0;
    let newLastMonth = 0;
    let totalLifespanMonths = 0; // soma dos meses de vida de todos os clientes
    let clientsWithLifespan = 0;
    let clientCount = 0; // Clientes que já pagaram (confirmedCount > 0)

    for (const c of companies) {
      const isActive =
        c.status === "active" || !c.status || c.status === "trial";
      // Churn = conta REALMENTE cancelada. 'suspended' é inadimplência temporária
      // (quem já pagava e deixou de pagar) — não é churn; 'trial_expired' é trial
      // não convertido — também não é churn.
      const isRealChurn = c.status === "churned";
      const createdMonth = c.createdAt?.slice(0, 7);

      // Conta clientes do mês
      if (createdMonth === thisMonth) newThisMonth++;
      if (createdMonth === lastMonth) newLastMonth++;

      // Tempo de vida do cliente (meses desde criação até agora ou até o cancelamento)
      if (c.createdAt) {
        const created = new Date(c.createdAt);
        const endDate = isRealChurn && c.updatedAt ? new Date(c.updatedAt) : now;
        const months = Math.max(
          1,
          (endDate - created) / (1000 * 60 * 60 * 24 * 30),
        );
        totalLifespanMonths += months;
        clientsWithLifespan++;
      }

      // Billing data from Asaas
      const billing = billingData?.[c.cnpj];
      const confirmedCount = billing?.financial?.confirmedCount || 0;
      const isClient = confirmedCount > 0; // Já efetuou pagamento = cliente

      // Status de trial vs cliente
      if (c.trialInfo) {
        if (c.trialInfo.isExpired) {
          trialExpiredCount++;
        } else if (!isClient) {
          trialCount++;
        }
      }

      if (isClient) clientCount++;

      // Churn = conta realmente cancelada (churned) que JÁ FOI cliente pagante.
      // Trial não convertido (nunca pagou) e inadimplência (suspended) não contam.
      if (isRealChurn && isClient) churnedCount++;

      // MRR: Asaas é a fonte de verdade. Inclui apenas clientes que já pagaram.
      const subValue = billing?.subscriptions?.[0]?.value;
      const planPrice = c.plan?.monthlyPrice;
      const clientMrr = subValue || planPrice || 0;

      if (isActive && isClient) {
        mrr += Number(clientMrr);
        activeCount++;
      }

      // Financeiro com problema
      if (billing?.financial?.status === "overdue") {
        overdueCount++;
      }
    }

    const arpu = activeCount > 0 ? mrr / activeCount : 0;
    const arr = mrr * 12;
    const growthRate =
      newLastMonth > 0
        ? ((newThisMonth - newLastMonth) / newLastMonth) * 100
        : newThisMonth > 0
          ? 100
          : 0;

    // Churn Rate = churned / total de clientes que já existiam (mensal é aproximação)
    // Usando total de clientes como base
    const totalEverActive = activeCount + churnedCount;
    const churnRate =
      totalEverActive > 0 ? (churnedCount / totalEverActive) * 100 : 0;

    // Tempo médio de vida (meses)
    const avgLifespanMonths =
      clientsWithLifespan > 0 ? totalLifespanMonths / clientsWithLifespan : 0;

    // LTV = ARPU × tempo médio de vida em meses
    // Se não há churn, usa o tempo médio atual como estimativa conservadora
    const ltv =
      churnRate > 0
        ? arpu / (churnRate / 100) // LTV clássico: ARPU / churn rate mensal
        : arpu * Math.max(avgLifespanMonths, 12); // Sem churn: estima 12 meses mínimo

    return {
      mrr,
      arr,
      activeCount,
      clientCount,
      totalCount: companies.length,
      arpu,
      trialCount,
      trialExpiredCount,
      overdueCount,
      churnedCount,
      churnRate,
      ltv,
      avgLifespanMonths,
      newThisMonth,
      newLastMonth,
      growthRate,
    };
  }, [companies, billingData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
    }).format(value);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-foreground-strong tracking-tight">
            Dashboard
          </h2>
          <p className="text-foreground-muted mt-1">
            Visão geral do negócio — métricas SaaS
          </p>
        </div>

        <div className="flex items-center gap-3">
          <DashboardFilters
            filters={filters}
            onChange={setFilters}
            plans={availablePlans}
          />
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-500 text-white px-4 py-2.5 rounded-xl font-semibold transition-all shadow-lg shadow-cyan-900/20"
          >
            <PlusCircle className="w-5 h-5" />
            Nova Empresa
          </button>
        </div>
      </div>

      {/* Metrics Cards */}
      {metrics && (
        <div className="space-y-3">
          {/* Row 1: Revenue metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* MRR */}
            <div className="bg-surface/60 border border-border rounded-xl p-4 hover:border-border-strong transition-colors">
              <div className="flex items-center gap-2 text-foreground-muted text-xs font-medium uppercase tracking-wider mb-2">
                <DollarSign className="w-3.5 h-3.5" />
                MRR
              </div>
              <div className="text-xl font-bold text-emerald-700 dark:text-emerald-400">
                {formatCurrency(metrics.mrr)}
              </div>
              <div className="text-[11px] text-foreground-muted mt-1">
                ARR {formatCurrency(metrics.arr)} · {metrics.clientCount}{" "}
                pagante{metrics.clientCount !== 1 ? "s" : ""}
              </div>
            </div>

            {/* ARPU */}
            <div className="bg-surface/60 border border-border rounded-xl p-4 hover:border-border-strong transition-colors">
              <div className="flex items-center gap-2 text-foreground-muted text-xs font-medium uppercase tracking-wider mb-2">
                <BarChart3 className="w-3.5 h-3.5" />
                ARPU
              </div>
              <div className="text-xl font-bold text-cyan-700 dark:text-cyan-400">
                {formatCurrency(metrics.arpu)}
              </div>
              <div className="text-[11px] text-foreground-muted mt-1">
                receita média/cliente
              </div>
            </div>

            {/* LTV */}
            <div className="bg-surface/60 border border-border rounded-xl p-4 hover:border-border-strong transition-colors">
              <div className="flex items-center gap-2 text-foreground-muted text-xs font-medium uppercase tracking-wider mb-2">
                <Heart className="w-3.5 h-3.5" />
                LTV
              </div>
              <div className="text-xl font-bold text-violet-700 dark:text-violet-400">
                {formatCurrency(metrics.ltv)}
              </div>
              <div className="text-[11px] text-foreground-muted mt-1">
                {metrics.churnRate > 0
                  ? `ARPU ÷ ${metrics.churnRate.toFixed(1)}% churn`
                  : `~${Math.round(metrics.avgLifespanMonths)}m vida média`}
              </div>
            </div>

            {/* LTV:CAC placeholder */}
            <div className="bg-surface/60 border border-border rounded-xl p-4 hover:border-border-strong transition-colors">
              <div className="flex items-center gap-2 text-foreground-muted text-xs font-medium uppercase tracking-wider mb-2">
                <Infinity className="w-3.5 h-3.5" />
                LTV:CAC
              </div>
              <div className="text-xl font-bold text-foreground-muted">—</div>
              <div className="text-[11px] text-foreground-muted mt-1">
                configurar CAC
              </div>
            </div>
          </div>

          {/* Row 2: Growth & Health metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Clientes Ativos */}
            <div className="bg-surface/60 border border-border rounded-xl p-4 hover:border-border-strong transition-colors">
              <div className="flex items-center gap-2 text-foreground-muted text-xs font-medium uppercase tracking-wider mb-2">
                <Users className="w-3.5 h-3.5" />
                Clientes
              </div>
              <div className="text-xl font-bold text-foreground-strong">
                {metrics.clientCount}
              </div>
              <div className="text-[11px] text-foreground-muted mt-1">
                {metrics.trialCount > 0
                  ? `${metrics.trialCount} em trial · `
                  : ""}
                {metrics.totalCount} total
              </div>
            </div>

            {/* Growth */}
            <div className="bg-surface/60 border border-border rounded-xl p-4 hover:border-border-strong transition-colors">
              <div className="flex items-center gap-2 text-foreground-muted text-xs font-medium uppercase tracking-wider mb-2">
                <TrendingUp className="w-3.5 h-3.5" />
                Novos/Mês
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xl font-bold text-foreground-strong">
                  {metrics.newThisMonth}
                </span>
                {metrics.growthRate !== 0 && (
                  <span
                    className={`flex items-center text-xs font-medium ${metrics.growthRate > 0 ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400"}`}
                  >
                    {metrics.growthRate > 0 ? (
                      <ArrowUpRight className="w-3 h-3" />
                    ) : (
                      <ArrowDownRight className="w-3 h-3" />
                    )}
                    {Math.abs(metrics.growthRate).toFixed(0)}%
                  </span>
                )}
              </div>
              <div className="text-[11px] text-foreground-muted mt-1">
                vs {metrics.newLastMonth} mês anterior
              </div>
            </div>

            {/* Churn */}
            <div className="bg-surface/60 border border-border rounded-xl p-4 hover:border-border-strong transition-colors">
              <div className="flex items-center gap-2 text-foreground-muted text-xs font-medium uppercase tracking-wider mb-2">
                <UserMinus className="w-3.5 h-3.5" />
                Churn
              </div>
              <div
                className={`text-xl font-bold ${metrics.churnRate > 5 ? "text-red-700 dark:text-red-400" : metrics.churnRate > 0 ? "text-yellow-700 dark:text-yellow-400" : "text-emerald-700 dark:text-emerald-400"}`}
              >
                {metrics.churnRate.toFixed(1)}%
              </div>
              <div className="text-[11px] text-foreground-muted mt-1">
                {metrics.churnedCount} cancelado
                {metrics.churnedCount !== 1 ? "s" : ""}
              </div>
            </div>

            {/* Inadimplência */}
            <div className="bg-surface/60 border border-border rounded-xl p-4 hover:border-border-strong transition-colors">
              <div className="flex items-center gap-2 text-foreground-muted text-xs font-medium uppercase tracking-wider mb-2">
                <Percent className="w-3.5 h-3.5" />
                Inadimplentes
              </div>
              <div
                className={`text-xl font-bold ${metrics.overdueCount > 0 ? "text-red-700 dark:text-red-400" : "text-emerald-700 dark:text-emerald-400"}`}
              >
                {metrics.overdueCount}
              </div>
              <div className="text-[11px] text-foreground-muted mt-1">
                {metrics.activeCount > 0
                  ? (
                      (metrics.overdueCount / metrics.activeCount) *
                      100
                    ).toFixed(1)
                  : 0}
                % da base
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trend Chart */}
      <TrendChart companies={allCompanies} billingData={billingData} />

      <CompanyTable
        data={companies}
        isLoading={isLoading}
        onEdit={openEditModal}
        billingData={billingData}
      />

      {/* Billing Audit */}
      <BillingAuditPanel />

      {/* AI CFO Assistant */}
      <CFOAssistant
        metrics={metrics}
        companies={allCompanies}
        billingData={billingData}
        trendData={trendData}
      />

      {/* Modal for adding/editing company */}
      {showModal && (
        <CompanyFormModal
          editingCompany={editingCompany}
          availablePlans={availablePlans}
          onClose={closeModal}
          onSaved={handleModalSaved}
        />
      )}
    </div>
  );
}
